import { useState, type ComponentProps, type ReactNode } from 'react';
import { CircleNotchIcon, StopIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { AgentRunSummary } from '@erp/rpc';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from 'src/components/ui/alert-dialog';
import { Button, buttonVariants } from 'src/components/ui/button';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { toast } from 'src/components/ui/toast';
import {
  useCancelAgentRun,
  useSetStopAfterCurrentTask,
} from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';

type StopTiming = 'after_task' | 'now';

function isStopTiming(value: string): value is StopTiming {
  return value === 'after_task' || value === 'now';
}

/** Server error message when the mutation surfaced one, else the fallback. */
function mutationErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

interface AgentStopButtonProps {
  latestRun: AgentRunSummary | null;
  /**
   * Task-loop (orchestrated) agents work through tasks one at a time, so they
   * can also stop between tasks. Other agents only offer an immediate stop.
   */
  supportsStopAfterTask: boolean;
}

/**
 * Stop for an agent run. For task-loop agents, Stop asks *when*: after the
 * current task (the default: nothing is left half-done) or right now. While a
 * stop-after-task request is pending the button reads "Stopping after task",
 * and reopening it lets the user keep the agent running or stop it now.
 */
export function AgentStopButton({
  latestRun,
  supportsStopAfterTask,
}: AgentStopButtonProps) {
  const gt = useGT();
  const cancel = useCancelAgentRun();
  const setStopAfterTask = useSetStopAfterCurrentTask();
  const [timing, setTiming] = useState<StopTiming>('after_task');

  const isRunning = latestRun?.status === 'running';
  const stopPending =
    isRunning && supportsStopAfterTask && !!latestRun.stopAfterTaskRequestedAt;
  const busy = cancel.isPending || setStopAfterTask.isPending;

  async function stopNow() {
    if (!latestRun) return;
    try {
      await cancel.mutateAsync({ agentRunId: latestRun.id });
      toast.success(
        gt('Agent stopped', {
          context: 'Toast when agent run stops successfully',
        }),
      );
    } catch (error) {
      toast.error(
        mutationErrorMessage(
          error,
          gt('Failed to stop agent', {
            context: 'Toast when stopping agent fails',
          }),
        ),
      );
    }
  }

  async function updateStopAfterTask(stop: boolean) {
    if (!latestRun) return;
    try {
      await setStopAfterTask.mutateAsync({ agentRunId: latestRun.id, stop });
      toast.success(
        stop
          ? gt('Agent will stop after its current task', {
              context:
                'Toast after asking an agent to stop once it finishes its current task',
            })
          : gt('Agent will keep running', {
              context:
                'Toast after withdrawing a request to stop an agent after its current task',
            }),
      );
    } catch (error) {
      toast.error(
        mutationErrorMessage(
          error,
          gt('Failed to update the agent', {
            context:
              'Toast when requesting or withdrawing a stop after the current task fails',
          }),
        ),
      );
    }
  }

  return (
    <AlertDialog
      onOpenChange={(open) => {
        // Every fresh Stop starts from the safe default.
        if (open) setTiming('after_task');
      }}
    >
      <AlertDialogTrigger asChild>
        <StopTriggerButton
          stopPending={stopPending}
          disabled={busy || (!stopPending && !isRunning)}
        />
      </AlertDialogTrigger>
      <AlertDialogContent>
        {stopPending ? (
          <PendingStopContent
            wrappingUp={!!latestRun.stopAfterTaskAcceptedAt}
            onKeepRunning={() => void updateStopAfterTask(false)}
            onStopNow={() => void stopNow()}
          />
        ) : (
          <ConfirmStopContent
            stopAfterTask={supportsStopAfterTask && timing === 'after_task'}
            timingChoice={
              supportsStopAfterTask ? (
                <StopTimingChoice value={timing} onChange={setTiming} />
              ) : null
            }
            onStopAfterTask={() => void updateStopAfterTask(true)}
            onStopNow={() => void stopNow()}
          />
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}

function StopTriggerButton({
  stopPending,
  disabled,
  ...triggerProps
}: {
  stopPending: boolean;
  disabled: boolean;
} & ComponentProps<'button'>) {
  if (stopPending) {
    return (
      <Button size="sm" variant="outline" disabled={disabled} {...triggerProps}>
        <CircleNotchIcon className="h-4 w-4 mr-1 animate-spin" />
        <T context="Button state while an agent finishes its current task before stopping">
          Stopping after task
        </T>
      </Button>
    );
  }
  return (
    <Button
      size="sm"
      variant="destructive"
      disabled={disabled}
      {...triggerProps}
    >
      <StopIcon className="h-4 w-4 mr-1" />
      <T context="Button to stop running agent">Stop</T>
    </Button>
  );
}

/**
 * The "Stop agent?" prompt. With a timing choice (task loops) the confirm
 * button follows the selection; without one it always stops now.
 */
function ConfirmStopContent({
  stopAfterTask,
  timingChoice,
  onStopAfterTask,
  onStopNow,
}: {
  stopAfterTask: boolean;
  timingChoice: ReactNode;
  onStopAfterTask: () => void;
  onStopNow: () => void;
}) {
  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>
          <T context="Confirm stopping an agent run">Stop agent?</T>
        </AlertDialogTitle>
        {!timingChoice && (
          <AlertDialogDescription>
            <StopNowExplanation />
          </AlertDialogDescription>
        )}
      </AlertDialogHeader>
      {timingChoice}
      <AlertDialogFooter>
        <AlertDialogCancel>
          <T context="Dismiss confirmation dialog">Cancel</T>
        </AlertDialogCancel>
        {stopAfterTask ? (
          <AlertDialogAction onClick={onStopAfterTask}>
            <T context="Confirm stopping an agent once its current task is done">
              Stop after task
            </T>
          </AlertDialogAction>
        ) : (
          <AlertDialogAction
            onClick={onStopNow}
            className={buttonVariants({ variant: 'destructive' })}
          >
            <T context="Confirm stopping an agent immediately">Stop now</T>
          </AlertDialogAction>
        )}
      </AlertDialogFooter>
    </>
  );
}

function StopNowExplanation() {
  return (
    <T context="Explanation of stopping an agent run immediately">
      This cancels the current run and resets any in-progress tasks back to
      pending. If a workflow started this run, we'll also try to stop that
      workflow run so it doesn't restart the agent.
    </T>
  );
}

/** The two ways to stop a task-loop agent, as selectable cards. */
function StopTimingChoice({
  value,
  onChange,
}: {
  value: StopTiming;
  onChange: (value: StopTiming) => void;
}) {
  const gt = useGT();
  return (
    <RadioGroup
      value={value}
      onValueChange={(next) => {
        if (isStopTiming(next)) onChange(next);
      }}
      aria-label={gt('When to stop', {
        context: 'Accessible label for the choice of when to stop an agent',
      })}
    >
      <StopTimingOption
        value="after_task"
        title={
          <T context="Option to stop an agent once its current task is done">
            After the current task
          </T>
        }
        description={
          <T context="Explains stopping an agent once its current task is done">
            The agent finishes what it's working on and runs its cleanup steps,
            then stops instead of picking up the next task. Nothing is left
            half-done.
          </T>
        }
      />
      <StopTimingOption
        value="now"
        title={<T context="Option to stop an agent immediately">Right now</T>}
        description={<StopNowExplanation />}
      />
    </RadioGroup>
  );
}

function StopTimingOption({
  value,
  title,
  description,
}: {
  value: StopTiming;
  title: ReactNode;
  description: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors hover:bg-accent/50 has-[[data-checked]]:border-primary has-[[data-checked]]:bg-primary/5">
      <RadioGroupItem value={value} className="mt-0.5" />
      <span className="space-y-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-sm text-muted-foreground">
          {description}
        </span>
      </span>
    </label>
  );
}

/**
 * A pending stop-after-task request. Until the agent finishes its task the
 * user can withdraw it; once the agent is wrapping up (cleanup steps) only
 * Stop now remains.
 */
function PendingStopContent({
  wrappingUp,
  onKeepRunning,
  onStopNow,
}: {
  wrappingUp: boolean;
  onKeepRunning: () => void;
  onStopNow: () => void;
}) {
  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>
          <T context="Title while an agent finishes its current task before stopping">
            Stopping after the current task
          </T>
        </AlertDialogTitle>
        <AlertDialogDescription>
          {wrappingUp ? (
            <T context="Explains that an agent finished its task and is running its cleanup steps before stopping">
              The agent has finished its task and is running its cleanup steps
              before it stops. You can still stop it right now to skip them.
            </T>
          ) : (
            <T context="Explains a pending request to stop an agent after its current task">
              The agent is finishing the task it's working on. It will then run
              its cleanup steps and stop instead of picking up the next task.
              You can let it keep going, or stop it right now and reset the
              in-progress task back to pending.
            </T>
          )}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>
          <T context="Dismiss dialog">Close</T>
        </AlertDialogCancel>
        {!wrappingUp && (
          <AlertDialogAction
            onClick={onKeepRunning}
            className={buttonVariants({ variant: 'outline' })}
          >
            <T context="Withdraw a request to stop an agent after its current task">
              Keep running
            </T>
          </AlertDialogAction>
        )}
        <AlertDialogAction
          onClick={onStopNow}
          className={buttonVariants({ variant: 'destructive' })}
        >
          <T context="Confirm stopping an agent immediately">Stop now</T>
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  );
}
